import { AxiosError, AxiosResponse } from 'axios'
import _ from 'lodash'
import React, {
  ChangeEvent,
  Fragment,
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Link, useHistory, useLocation } from 'react-router-dom'

import { IAutoSuggestProps, matchInArray } from './AutoSuggest.model'
import {
  StyledAlternativesText,
  StyledAutoSuggestResultItem,
  StyledAutoSuggestWrapper,
  StyledCounterContainer,
  StyledCounterNumber,
  StyledDesktopWrapper,
  StyledLinkWithIcon,
  StyledLoadingSpinnerWrapper,
  StyledNoResults,
  StyledSectionHeader,
  StyledSectionTitle,
  StyledRecentSearchHeader
} from './AutoSuggest.styles'
import { ClinTheme } from '../../../ClinTheme'
import { AnnounceMode } from '../../../components/ClinAnnounceBar/ClinAnnounceBar'
import {
  NewFeatureElements,
  PageNames
} from '../../../components/ClinNewFeatureTooltip/ClinNewFeatureTooltip.types'
import { ClinText } from '../../../components/ClinText'
import {
  isAusGaUser,
  isAusMaUser,
  isAusUser,
  nonMaUsers,
  ProductItemAvailabilityStatus,
  UserRole,
  UserRoleRecord
} from '../../../constants'
import { useAppContext } from '../../../context/app'
import {
  clearAnnounceEvent,
  createAnnounceEvent
} from '../../../events/AnnounceEvent'
import { useNewFeaturesList } from '../../../hooks/useNewFeaturesList/useNewFeaturesList'
import { AnalyticsEvent } from '../../../services/Analytics'
import analyticsServiceSingleton from '../../../services/Analytics/initAnalytics'
import {
  AuthError,
  cancelGetPriceAndAvailabilityForSku,
  cancelGetProductsSuggestion,
  getPriceAndAvailabilityForSku,
  getProductById,
  getProductsSuggestion,
  getWarehouseForCodes
} from '../../../services/ApiService'
import {
  CatalogDto,
  CatalogDtoSuggestionDto
} from '../../../types/swaggerTypes'
import { ClinIcon } from '../../../components/ClinIcon'
import { ClinIconPathName } from '../../../components/ClinIcon/ClinIcon.paths'
import {
  CatalogueItemConstants,
  catalogueItemStatusMapper,
  getCatalogueItemStatus,
  getMatchingDefaultWarehouseCodes,
  ICatalogItemDto,
  sortCatalogItemsByAvailability,
  sortCatalogItemsByAvailabilityAus
} from '../../../pages/Products/ProductVariant/ProductVariant.models'
import { IAugmentedCatalogItemDto } from '../../../services/PortfolioJourneys/PortfolioJourney.types'
import { getVariantInformationForCatalogItem } from '../../../services/PortfolioJourneys/PortfolioJourney.model'
import { CountryAlphaCodes } from '../../../constants/countryAlpha2Codes'
import { useErrorMessage } from '../../../utils/useErrorMessage'
import {
  FixedWidthCell,
  StyledClinTable,
  StyledClinTableBodyCell,
  StyledClinTableRow,
  StyledMobileWrapper,
  StyledSpinnerCell,
  StyledStrengthCell
} from '../../../pages/Products/ProductVariant/ProductVariant.styles'
import { ClinTableBodyCell } from '../../../components/ClinTable/ClinTable'
import { TypographyVariant } from '../../../components/ClinText/ClinText.styles'
import { ClinSpinner } from '../../../components/ClinSpinner'
import StatusColumn from '../../../pages/Products/ProductVariant/StatusColumn/StatusColumn'
import { numberToCurrencyString } from '../../../utils/numberToCurrencyString'
import { getBrowserLocale } from '../../../utils/getBrowserLocale'
import {
  COMPREHENSIVE_DOSAGE_FORMS,
  findPartialDosageFormMatch,
  parseSearchQuery
} from '../../../utils/dosageFormUtils'
import { Basket } from '../../../pages/Products/ProductVariant/Basket/Basket'
import { DateTime } from 'luxon'
import i18n from '../../../i18n/config'
import { useFeatureFlags } from '../../../context/featureFlags/FeatureFlagContext'
import { FeatureFlagKeys } from '../../../context/featureFlags/FeatureFlagKeys'
import { ClinDrawer } from '../../../components/ClinDrawer'
import { BasketDrawer } from '../../BasketDrawer/BasketDrawer'
import { ClinButton } from '../../../components/ClinButton'
import MobileItem from '../../../pages/Products/ProductVariant/MobileItem/MobileItem'
import { CheckoutDrawer } from '../../CheckoutDrawer/CheckoutDrawer'

export interface ISearchStylesProps {
  /** Show suggestions below search */
  showSuggestions?: boolean
  /** Show suggestions below search */
  showGlowEffect?: boolean
  /** Whether this AutoSuggest is used in header context (affects styling) */
  isHeader?: boolean
  /** Show suggestions below search */
  hasSuggestions?: boolean
}

interface IAutoSuggestContainerProps {
  /** App path/locations to hide this component on */
  pathsToHide?: string[]
  /** Amount of debounce time in milliseconds */
  debounceTimeMs?: number
  /** Initial query string */
  initialQuery?: string
  /** Number of results to display */
  maxResultsToDisplay?: number
  /** No results text */
  noResultsText?: string
  /** No results text for ma users */
  maUserNoResultsText?: string
  /** No results path */
  noResultsPath?: string
  /** MA user path */
  maUserPath?: string
  /** Suggestion text */
  suggestionText?: string
  /** Suggestion path */
  suggestionPath?: string
  /** Placeholder text */
  placeholderText?: string
  /** Optional border radious for last li element in suggestions*/
  borderRadiusLastLiElement?: string
  /** Optional parameter color for suggestion text*/
  suggestionTextColor?: string
  /** Optional parameter padding for suggestion text*/
  suggestionTextPadding?: string
  /** Whether to show icons in suggestion items and links */
  showIcons?: boolean
  /** Recent searches to display when input is focused but empty */
  recentSearches?: CatalogDtoSuggestionDto[]
  /** Whether to show recent searches on focus (default: true) */
  showRecentSearches?: boolean
  /** Search component to pass through */
  searchComponent(props: IAutoSuggestProps): ReactNode
  /** Call back to reveal query */
  handleOnSearch?: (query: string) => void
  /** Call back to broadcast onChange */
  handleOnChange?: (query: string) => void
  /** Callback to set isNotFound */
  setIsNotFound?: (item: boolean) => void
  /** Callback to set isAliasesDisplayed */
  setIsAliasesDisplayed?: (item: boolean) => void
  /** To check if aliases are displayed */
  isAliasesDisplayed?: boolean
  /** Show products on exact search match */
  isShowExactMatch?: boolean
  /** Callback to set current query */
  setCurrentQuery?: (query: string) => void
  /** Whether to show no results section within AutoSuggest (default: true) */
  showNoResults?: boolean
  /** Whether this AutoSuggest is used in header context (affects styling) */
  isHeader?: boolean
}

// Add new interface for structured suggestions
interface GroupedSuggestions {
  products: CatalogDtoSuggestionDto[]
  programs: CatalogDtoSuggestionDto[]
  productsCount: number
  programsCount: number
}

export const AutoSuggest: FunctionComponent<IAutoSuggestContainerProps> = ({
  pathsToHide,
  searchComponent,
  debounceTimeMs = 500,
  handleOnSearch,
  handleOnChange,
  initialQuery = '',
  borderRadiusLastLiElement,
  maxResultsToDisplay = 10,
  suggestionTextColor,
  suggestionTextPadding,
  suggestionText = 'Looking for something else?',
  suggestionPath = '/products/catalogue',
  maUserPath = '/results',
  noResultsText = 'View all products and programs',
  maUserNoResultsText = 'View all programs',
  noResultsPath,
  placeholderText,
  setIsNotFound,
  isAliasesDisplayed,
  setIsAliasesDisplayed,
  showIcons = true,
  isShowExactMatch = true,
  recentSearches,
  showRecentSearches = true,
  setCurrentQuery,
  showNoResults = true,
  isHeader = false
}: IAutoSuggestContainerProps) => {
  const { t } = useTranslation()
  const {
    dispatch,
    userRole,
    portfolioCountryCode,
    institute,
    defaultShippingAddress
  } = useAppContext()
  const { useFeatureFlag } = useFeatureFlags()

  const isMaUser = !!(userRole && UserRoleRecord[userRole as UserRole].isMaUser)
  const location = useLocation()
  const history = useHistory()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false)
  const [query, setQuery] = useState<string>(initialQuery)
  const [suggestions, setSuggestions] = useState<CatalogDtoSuggestionDto[]>([])
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [isExactMatch, setIsExactMatch] = useState(false)
  const [catalogItems, setCatalogItems] = useState<ICatalogItemDto[]>([])
  const [filteredCatalogItems, setFilteredCatalogItems] = React.useState<
    ICatalogItemDto[]
  >([])
  const [catalogItemsWithPrice, setCatalogItemsWithPrice] = useState<
    ICatalogItemDto[]
  >([])
  const [product, setProduct] = React.useState<CatalogDto>()
  const [isExactMatchLoading, setIsExactMatchLoading] = useState<boolean>(false)
  const [hoveredRow, setHoveredRow] = useState<string | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)
  const pricesLoadedRef = useRef(false)
  const [isCheckoutDrawerOpen, setIsCheckoutDrawerOpen] =
    useState<boolean>(false)

  // AC7: State persistence for exact matches
  const [lastExactMatchProduct, setLastExactMatchProduct] = useState<string>('')

  // Store original API counts for header context
  const [apiProductsCount, setApiProductsCount] = useState<number>(0)
  const [apiProgramsCount, setApiProgramsCount] = useState<number>(0)

  // Enhanced search states for dosage form filtering
  const [dosageFormFilter, setDosageFormFilter] = useState<string>('')
  const [hasAppliedDosageFilter, setHasAppliedDosageFilter] =
    useState<boolean>(false)

  // Track when primary search returned no results (for showing "No results matching..." message)
  const [primarySearchReturnedNoResults, setPrimarySearchReturnedNoResults] =
    useState<boolean>(false)

  const enableNewBasketDrawer = useFeatureFlag(FeatureFlagKeys.NewBasketDrawer)
  const isNewCheckoutPage = useFeatureFlag(
    FeatureFlagKeys.NewOneStepCheckoutPage
  )
  const enableNewCheckoutDrawer = useFeatureFlag(
    FeatureFlagKeys.NewGaCheckoutDrawer
  )

  // Use the shared comprehensive dosage forms constant
  const comprehensiveDosageForms = React.useMemo(
    () => COMPREHENSIVE_DOSAGE_FORMS,
    []
  )

  const getCatalogueItemColumns = (portfolioCountryCode: string) => [
    {
      name: 'strength',
      viewName: 'Strength',
      translationKey: 'product_variants:column_titles.strength',
      width: '100%',
      minWidth: '145px',
      maxWidth: '160px',
      isSortable: false,
      renderValue: (catalogueItem: IAugmentedCatalogItemDto) => (
        <>
          {catalogueItem.products.map((product: any, idx: number) => (
            <StyledStrengthCell
              key={`desktop-catalogue-item-${
                catalogueItem.itemId || catalogueItem.item.sku || 'unknown'
              }-product-${product.genericName || product.sku || idx}`}
            >
              <ClinText
                variant={TypographyVariant.SmallUI}
                lineHeight={ClinTheme.lineHeights.heading[0]}
                fontSize={ClinTheme.fontSizes[1]}
              >
                {product.genericName}
              </ClinText>
              <ClinText
                lineHeight={ClinTheme.lineHeights.heading[0]}
                fontSize={ClinTheme.fontSizes[1]}
              >
                {product.strength}
              </ClinText>
            </StyledStrengthCell>
          ))}
        </>
      )
    },
    {
      name: 'brand',
      viewName: 'Brand',
      translationKey: 'product_variants:column_titles.brand',
      width: '100%',
      isSortable: false,
      renderValue: (catalogueItem: IAugmentedCatalogItemDto) => (
        <ClinText
          fontSize={ClinTheme.fontSizes[1]}
          lineHeight={ClinTheme.lineHeights.heading[0]}
          whiteSpace="nowrap"
        >
          {catalogueItem.item.brand ?? '--'}
        </ClinText>
      )
    },
    {
      name: 'dosageFormAndPackSize',
      viewName: 'Dosage form and pack size',
      translationKey:
        'product_variants:column_titles.dosage_form_and_pack_size',
      width: '100%',
      minWidth: '115px',
      maxWidth: '180px',
      isSortable: false,
      renderValue: (catalogueItem: IAugmentedCatalogItemDto) => (
        <>
          <ClinText
            fontSize={ClinTheme.fontSizes[1]}
            lineHeight={ClinTheme.lineHeights.heading[0]}
          >
            {catalogueItem.item.formulation}
          </ClinText>
          <ClinText
            fontSize={ClinTheme.fontSizes[1]}
            lineHeight={ClinTheme.lineHeights.heading[0]}
          >
            {catalogueItem.item.packSize}
          </ClinText>
        </>
      )
    },
    {
      name: 'availability',
      viewName: 'Availability',
      translationKey: 'product_variants:column_titles.availability',
      width: '100%',
      renderValue: function column(catalogueItem: IAugmentedCatalogItemDto) {
        const catalogueItemStatus = getCatalogueItemStatus(
          catalogueItem,
          portfolioCountryCode,
          catalogueItem.priceAvailability
        )
        const isAvailable =
          catalogueItem.availableStatus ===
          ProductItemAvailabilityStatus.AVAILABLE
        const isPriceLoading =
          isAvailable && catalogueItem.unitPrice === undefined

        return (
          <>
            {isPriceLoading ? (
              <StyledSpinnerCell>
                <ClinSpinner size={ClinTheme.space[3]} />
              </StyledSpinnerCell>
            ) : (
              <StatusColumn
                availableQuantity={
                  catalogueItem.priceAvailability?.availableQuantity
                }
                catalogueItemStatus={catalogueItemStatus}
              />
            )}
          </>
        )
      }
    },
    {
      name: 'price',
      viewName: 'Pack price',
      translationKey: 'product_variants:column_titles.pack_price',
      width: '100%',
      isSortable: false,
      renderValue: (catalogueItem: IAugmentedCatalogItemDto) => {
        const isAvailable =
          catalogueItem.availableStatus ===
          ProductItemAvailabilityStatus.AVAILABLE
        const isPriceLoading =
          isAvailable && catalogueItem.unitPrice === undefined
        return (
          <ClinText
            fontSize={ClinTheme.fontSizes[1]}
            lineHeight={ClinTheme.lineHeights.heading[0]}
            color={ClinTheme.colors.graphiteBlue}
            textAlign="center"
            whiteSpace="nowrap"
          >
            {isPriceLoading ? (
              <StyledSpinnerCell>
                <ClinSpinner size={ClinTheme.space[3]} />
              </StyledSpinnerCell>
            ) : isAvailable &&
              catalogueItem.unitPrice !== undefined &&
              catalogueItem.currencyCode ? (
              numberToCurrencyString(
                catalogueItem.unitPrice,
                getBrowserLocale(),
                {
                  style: 'currency',
                  currency: catalogueItem.currencyCode,
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }
              )
            ) : (
              '--'
            )}
          </ClinText>
        )
      }
    },
    {
      name: 'estimatedDelivery',
      viewName: (
        <>
          <div style={{ fontWeight: ClinTheme.fontWeights.medium }}>
            {t('product_variants:column_titles.estimated_delivery')}
          </div>
          <span style={{ fontWeight: ClinTheme.fontWeights.normal }}>
            {t('product_variants:column_titles.order_time')}
          </span>
        </>
      ),
      translationKey: 'product_variants:column_titles.estimated_delivery',
      width: '100%',
      isSortable: false,
      renderValue: (catalogueItem: IAugmentedCatalogItemDto) => {
        const catalogueItemDeliveryDate = getCatalogueItemDeliveryDate(
          catalogueItem,
          portfolioCountryCode
        )

        return (
          <ClinText
            variant={TypographyVariant.SmallUI}
            lineHeight={ClinTheme.lineHeights.small}
            fontSize={ClinTheme.fontSizes[0]}
            whiteSpace="nowrap"
          >
            {catalogueItemDeliveryDate}
          </ClinText>
        )
      }
    },
    {
      name: 'Quantity',
      viewName: 'Quantity',
      translationKey: 'product_variants:column_titles.estimated_delivery',
      width: '100%',
      minWidth: '160px',
      maxWidth: '160px',
      isSortable: false,
      renderValue: (catalogItem: IAugmentedCatalogItemDto) => {
        const catalogueItemStatus = getCatalogueItemStatus(
          catalogItem,
          portfolioCountryCode,
          catalogItem.priceAvailability
        )
        const productAvailabilityStatus =
          catalogueItemStatusMapper[catalogueItemStatus]

        return (
          <Basket
            inlineWidth="166"
            maxQuantity={catalogItem.item.maximumOrderQuantity || 10000}
            basketType="inline"
            userCountry={portfolioCountryCode}
            userRole={userRole}
            product={product!}
            catalogItem={{
              ...catalogItem,
              availableStatus: productAvailabilityStatus
            }}
            setIsDrawerOpen={setIsDrawerOpen}
            handleClickedOutside={resetAll}
            handleGoToBasket={handleGoToBasket}
            handleProceedToCheckout={
              isNewCheckoutPage
                ? handleProceedToCheckout
                : handleProceedToCheckoutOld
            }
          />
        )
      }
    },
    {
      name: 'view',
      viewName: 'Actions',
      translationKey: 'product_variants:column_titles.estimated_delivery',
      width: '100%',
      minWidth: '58px',
      maxWidth: '58px',
      isSortable: false,
      renderValue: () => (
        <ClinButton
          variant="linkButton"
          onClick={() => handleGoToProductDetails()}
        >
          {t('common:buttons.view')}
        </ClinButton>
      )
    }
  ]

  const columns = getCatalogueItemColumns(portfolioCountryCode)

  const getCatalogueItemDeliveryDate = (
    catalogueItem: IAugmentedCatalogItemDto,
    portfolioCountryCode: string
  ) => {
    const catalogueItemStatus = getCatalogueItemStatus(
      catalogueItem,
      portfolioCountryCode,
      catalogueItem.priceAvailability
    ) as CatalogueItemConstants

    const eDate = catalogueItem.expectedDeliveryDates?.find(
      (e: any) => e.shipToCountry === portfolioCountryCode
    )

    if (
      [
        CatalogueItemConstants.RequestQuote,
        CatalogueItemConstants.BackOrder,
        CatalogueItemConstants.OutOfStock
      ].includes(catalogueItemStatus)
    ) {
      return '--'
    }

    if (!eDate?.expectedDeliveryDate) {
      return '--'
    }

    if (catalogueItem.showDistributorInfo) {
      return '--'
    }

    // Parse the expected delivery date
    const deliveryDate = DateTime.fromISO(eDate.expectedDeliveryDate, {
      zone: 'utc'
    })

    // Get today's date
    const today = DateTime.now().startOf('day')

    // If delivery date is today, add one day
    const adjustedDeliveryDate = deliveryDate.hasSame(today, 'day')
      ? deliveryDate.plus({ days: 1 })
      : deliveryDate

    // Format the date
    return adjustedDeliveryDate.setLocale(i18n.language).toFormat('cccc MMM d')
  }
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const [hasSearched, setHasSearched] = useState<boolean>(false)

  // Ref to track if component is mounted to prevent memory leaks
  const isMountedRef = useRef(true)
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  //check if user did't see this feature and if not return tooltip object with title and description.
  const { newFeaturesList } = useNewFeaturesList(PageNames.GlobalSearch)
  const featureExists: boolean =
    !!newFeaturesList[NewFeatureElements.GlobalSearchNewUserFeature]

  // Use the shared partial dosage form matching function
  const findPartialDosageFormMatchLocal = React.useCallback(
    (word: string) => findPartialDosageFormMatch(word),
    []
  )

  // Use the shared search query parsing function
  const parseSearchQueryLocal = React.useCallback(
    (searchString: string, isExactMatch: boolean = false) =>
      parseSearchQuery(searchString, isExactMatch),
    []
  )

  // Helper function to filter suggestions by dosage form - works at item level within products
  const filterSuggestionsByDosageForm = useCallback(
    (
      suggestions: CatalogDtoSuggestionDto[],
      dosageFormFilter: string
    ): CatalogDtoSuggestionDto[] => {
      if (!dosageFormFilter || dosageFormFilter.trim() === '') {
        return suggestions
      }

      const filterTerm = dosageFormFilter.toLowerCase().trim()

      return suggestions
        .map((suggestion) => {
          // Filter items within each suggestion based on formulation
          const filteredItems =
            suggestion.document.items?.filter((item) => {
              const formulation = item.item?.formulation?.toLowerCase() || ''
              const formulationPackSize =
                item.formulationPackSize?.toLowerCase() || ''

              // Direct inclusion matching (exact or substring)
              if (
                formulation.includes(filterTerm) ||
                formulationPackSize.includes(filterTerm)
              ) {
                return true
              }

              // Handle hyphenated variations (e.g., "prolonged-release tablet")
              const normalizedFormulation = formulation.replace(/-/g, ' ')
              const normalizedFilter = filterTerm.replace(/-/g, ' ')
              if (
                normalizedFormulation.includes(normalizedFilter) ||
                formulationPackSize
                  .replace(/-/g, ' ')
                  .includes(normalizedFilter)
              ) {
                return true
              }

              // Enhanced partial matching - check if filter term partially matches any dosage form in formulation
              const formulationWords = formulation.split(/\s+/)
              const formulationPackSizeWords = formulationPackSize.split(/\s+/)
              const allWords = [
                ...formulationWords,
                ...formulationPackSizeWords
              ]

              for (const word of allWords) {
                // Try to find if this word partially matches our filter term
                const partialMatch = findPartialDosageFormMatchLocal(filterTerm)
                if (partialMatch && word.includes(partialMatch.toLowerCase())) {
                  return true
                }

                // Also check if the word in formulation partially matches the filter
                const wordMatch = findPartialDosageFormMatchLocal(word)
                if (wordMatch && filterTerm.includes(wordMatch.toLowerCase())) {
                  return true
                }
              }

              return false
            }) || []

          // Only return suggestions that have matching items
          if (filteredItems.length > 0) {
            return {
              ...suggestion,
              document: {
                ...suggestion.document,
                items: filteredItems
              }
            }
          }

          return null
        })
        .filter(
          (suggestion): suggestion is CatalogDtoSuggestionDto =>
            suggestion !== null
        )
    },
    [findPartialDosageFormMatchLocal]
  )

  // Helper function to group suggestions by type
  const groupSuggestionsByType = (
    suggestions: CatalogDtoSuggestionDto[],
    productsCount: number,
    programsCount: number
  ): GroupedSuggestions => {
    const products = suggestions.filter((s) => s.type === 'product')
    const programs = suggestions.filter((s) => s.type === 'program')

    return {
      products,
      programs,
      productsCount,
      programsCount
    }
  }

  const resetAll = () => {
    setSuggestions([])
    setQuery('')
    setHighlightedIndex(0)
    setIsLoading(false)
    setShowSuggestions(false)
    setIsFocused(false)
    setHasSearched(false)
    setIsNotFound && setIsNotFound(false)
    setIsAliasesDisplayed && setIsAliasesDisplayed(false)
    setCurrentQuery && setCurrentQuery('')
    // Reset enhanced search states
    setDosageFormFilter('')
    setHasAppliedDosageFilter(false)
    // AC7: Reset exact match persistence state
    setIsExactMatch(false)
    setLastExactMatchProduct('')
    // Reset API counts
    setApiProductsCount(0)
    setApiProgramsCount(0)
    // Reset primary search failure state
    setPrimarySearchReturnedNoResults(false)
    pricesLoadedRef.current = false
  }

  const requestSuggestions = useCallback(
    (searchString: string) => {
      if (!searchString || searchString === '') {
        setSuggestions([])
        setHighlightedIndex(0)
        setIsLoading(false)
        setHasSearched(false)
        // Reset enhanced search states
        setDosageFormFilter('')
        setHasAppliedDosageFilter(false)
        // Reset primary search failure state
        setPrimarySearchReturnedNoResults(false)
        // Show suggestions if focused and we have recent searches to show and showRecentSearches is enabled
        setShowSuggestions(
          isFocused &&
            showRecentSearches &&
            !!(recentSearches && recentSearches.length > 0)
        )
        setIsNotFound && setIsNotFound(false)
        setIsAliasesDisplayed && setIsAliasesDisplayed(false)
        return
      }

      // Parse the search query to separate product name from dosage form
      // For exact matches, we'll update this after we know if we have an exact match
      const { productName, dosageForm } = parseSearchQueryLocal(
        searchString,
        false
      )

      // AC7: Check if this might be a dosage form modification of an existing exact match
      const potentialExactMatchParsing = parseSearchQueryLocal(
        searchString,
        true
      )
      const isLikelyDosageModification =
        lastExactMatchProduct &&
        potentialExactMatchParsing.productName &&
        lastExactMatchProduct
          .toLowerCase()
          .includes(potentialExactMatchParsing.productName.toLowerCase()) &&
        potentialExactMatchParsing.dosageForm

      // Set the detected dosage form for filtering
      if (dosageForm) {
        setDosageFormFilter(dosageForm)
        setHasAppliedDosageFilter(true)
      } else {
        setDosageFormFilter('')
        setHasAppliedDosageFilter(false)
      }

      // Use the product name for the initial search, or the full query if no dosage form detected
      const searchTerm = productName || searchString
      // Empty string means receiving programs and products simultaneously
      const userRoleFlag = isAusMaUser(portfolioCountryCode, userRole) // related to clos-1614 aus ma users can see products
        ? ''
        : isMaUser
        ? 'ManagedAccessProgram'
        : isAusGaUser(portfolioCountryCode, userRole) // added this condition related to clos-1610 aus ga users can not see access program
        ? 'Generic'
        : ''
      setIsLoading(true)
      setHasSearched(true)
      cancelGetProductsSuggestion()
      analyticsServiceSingleton.trackEvent(AnalyticsEvent.SubmitSearchQuery, {
        query: searchString,
        searchLocation: 'global',
        searchAPI: 'suggest'
      })
      getProductsSuggestion({
        query: searchTerm,
        limit: maxResultsToDisplay,
        derivedFromFilter: userRoleFlag,
        countryCode: portfolioCountryCode
      })
        .then((results) => {
          // Track if primary search returned no results
          const primaryHadNoResults =
            results.data.result.length === 0 && searchTerm.length > 0
          setPrimarySearchReturnedNoResults(primaryHadNoResults)

          // If no results found, try again with includeAliases: true
          if (primaryHadNoResults) {
            return getProductsSuggestion({
              query: searchTerm,
              limit: maxResultsToDisplay,
              derivedFromFilter: userRoleFlag,
              countryCode: portfolioCountryCode,
              includeAliases: true
            }).then((aliasResults) => {
              return {
                ...aliasResults,
                wasAliasSearch: true
              }
            })
          }
          return { ...results, wasAliasSearch: false }
        })
        .then((results) => {
          if (results.data.result.length === 1 && !isHeader) {
            setIsExactMatch(true)
            setIsExactMatchLoading(true)
            const productId = results.data.result[0].document.catalogDocumentId

            // AC7: Store the exact match product name for state persistence
            const productName =
              results.data.result[0].document.catalogItemName || ''
            setLastExactMatchProduct(productName)

            // Add the exact match product to recent searches
            updateRecentSearches(results.data.result[0])

            // AC2 & AC3: Re-parse the search string now that we know it's an exact match
            // This enables partial dosage form matching and ignoring unrecognized text
            const exactMatchParsing = parseSearchQueryLocal(searchString, true)
            const exactMatchDosageForm = exactMatchParsing.dosageForm

            // Update dosage form filter with the enhanced parsing result
            if (exactMatchDosageForm) {
              setDosageFormFilter(exactMatchDosageForm)
              setHasAppliedDosageFilter(true)
            }

            getProductById(productId)
              .then((response: AxiosResponse<CatalogDto>) => {
                const catalog = response.data
                setProduct(catalog)
                // Reset prices loaded flag when new product is set
                pricesLoadedRef.current = false

                // Derived from is MAP - don't redirect automatically, let user click on the suggestion
                if (catalog.derivedFrom === 'ManagedAccessProgram') {
                  // Just track the event but don't redirect automatically
                  analyticsServiceSingleton.trackEvent(
                    AnalyticsEvent.ProductReferralAccessProgram,
                    { productId }
                  )
                  // For programs, set exact match to false to show regular autosuggest results
                  setIsExactMatch(false)
                  setIsLoading(false)
                  setIsExactMatchLoading(false)
                  return
                }

                // Derived from is Generic and user MaOnly then show error
                const isNonMaUser = nonMaUsers.includes(userRole!)
                const isAusMa = isAusMaUser(portfolioCountryCode, userRole)

                // Its an ma user track analytics
                if (!isNonMaUser) {
                  setIsLoading(false)
                  analyticsServiceSingleton.trackEvent(
                    AnalyticsEvent.ProductReferralRoleError,
                    { productId }
                  )
                }

                // Only continue with second call if user roles allow
                if (isNonMaUser || isAusMa) {
                  // For exact matches, always show ALL variants regardless of dosage form
                  // This ensures "melat tablet" shows all melatonin variants, same as "melat"
                  const filteredCatalogItems = catalog.items

                  // For each catalogueItem get the matching default warehouses and save unique ones
                  const allWarehousesCodes: string[] = []
                  let catalogItems: ICatalogItemDto[] =
                    filteredCatalogItems.map((catalogItem) => {
                      // Get the warehouses that this item ships from
                      const availableWarehouseCodes: string[] =
                        getMatchingDefaultWarehouseCodes(
                          catalogItem.stockLevels,
                          portfolioCountryCode,
                          institute.data?.warehouses
                        )
                      // Make up a list of all codes so we can get more detail for them all
                      allWarehousesCodes.push(...availableWarehouseCodes)
                      // Augment object
                      return {
                        ...catalogItem,
                        warehouseCodes: availableWarehouseCodes,
                        warehouses: undefined //to call in API
                      }
                    })

                  // POC: Filtering duplicate variants will be implemented on the FE at some point in time, sadly the requirements are not up to par as we are
                  //filtering based on 5 arbitrary  out of ~120 properties considered for a variant on the product level.
                  // if (isAusUser(portfolioCountryCode)) {
                  //   catalogItems = filterVarietyCards(catalogItems)
                  // }

                  // Get the unique list of warehouse codes so we can batch call
                  const uniqueWarehousesCodes = _.uniq(allWarehousesCodes)

                  // Make the API calls and augment info onto catalog items
                  getWarehouseForCodes(uniqueWarehousesCodes)
                    .then((response) => {
                      const warehouses = response.data
                      // Add in warehouse DTO from BFF
                      catalogItems = catalogItems
                        .map((cat) => {
                          // Get the default warehouses full DTO for this catalog item
                          const defaultWarehouses = warehouses.filter((w) =>
                            w.warehouseCode
                              ? cat.warehouseCodes.includes(w.warehouseCode)
                              : false
                          )
                          // Add portfolio information
                          const augItem: IAugmentedCatalogItemDto =
                            getVariantInformationForCatalogItem(
                              cat,
                              portfolioCountryCode,
                              defaultWarehouses[0],
                              defaultWarehouses
                            )
                          return {
                            ...cat,
                            ...augItem,
                            warehouses: defaultWarehouses
                          }
                        })
                        .sort((a, b) =>
                          portfolioCountryCode === CountryAlphaCodes.Australia
                            ? sortCatalogItemsByAvailabilityAus(
                                a,
                                b,
                                portfolioCountryCode
                              )
                            : sortCatalogItemsByAvailability(
                                a,
                                b,
                                portfolioCountryCode
                              )
                        )

                      setCatalogItems(catalogItems)
                    })
                    .catch((error) => {
                      handleWarehouseError(error)
                    })
                } else {
                  setIsExactMatchLoading(false)
                }
              })
              .catch((error: AxiosError) => {
                handleProductError(error)
                setIsLoading(false)
              })
          } else {
            // AC7: Check if this should preserve exact match state (dosage form modification)
            if (isLikelyDosageModification && product) {
              // This is likely a dosage form modification of an existing exact match
              // Preserve the exact match state and just update the dosage filtering
              setIsExactMatch(true)

              if (potentialExactMatchParsing.dosageForm) {
                setDosageFormFilter(potentialExactMatchParsing.dosageForm)
                setHasAppliedDosageFilter(true)
              } else {
                setDosageFormFilter('')
                setHasAppliedDosageFilter(false)
              }

              // Re-filter the existing catalog items with the new dosage form
              // This will be handled by the filterHiddenVariants useEffect
            } else {
              setIsExactMatch(false)
              setLastExactMatchProduct('')
            }
          }

          return results
        })
        .then((results) => {
          setHighlightedIndex(-1)

          // Store original API counts for header context
          setApiProductsCount(results.data.productsCount || 0)
          setApiProgramsCount(results.data.programsCount || 0)

          // For suggestions view, always show all results without dosage form filtering
          // Dosage form filtering will only be applied in the exact match view
          const displayResults = results.data.result

          setSuggestions(displayResults)

          // Show suggestions when we have results and we have a search string
          const shouldShowSuggestions = searchString.length > 0

          setShowSuggestions(shouldShowSuggestions)
          setIsNotFound &&
            setIsNotFound(
              results.data.result.length === 0 && searchString.length > 0
            )
          setIsAliasesDisplayed &&
            setIsAliasesDisplayed(
              results.wasAliasSearch && results.data.result.length > 0
            )
          if (searchString && searchString !== '') {
            analyticsServiceSingleton.trackEvent(
              AnalyticsEvent.ResultsForSearchQuery,
              {
                query: searchString,
                numberResults: displayResults.length,
                originalResults: results.data.result.length,
                dosageFormApplied: dosageForm || null
              }
            )
          }
        })
        .catch((error: AxiosError) => {
          // If request is cancelled continue
          if (error.message === AuthError.RequestCancelled) {
            return
          }
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `There was an error fetching suggestions. ${error.message} ${
                error.code ? error.code : ''
              }`
            )
          )
          setShowSuggestions(false)
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [
      dispatch,
      isMaUser,
      maxResultsToDisplay,
      portfolioCountryCode,
      userRole,
      parseSearchQueryLocal,
      filterSuggestionsByDosageForm,
      isFocused,
      showRecentSearches,
      recentSearches,
      setIsNotFound,
      setIsAliasesDisplayed,
      setCurrentQuery,
      isShowExactMatch,
      institute.data?.warehouses
    ]
  )

  // Create a stable debounced function using useRef to prevent recreation
  // This ensures the 500ms debounce works correctly and isn't reset on every render
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const requestSuggestionsRef = useRef(requestSuggestions)

  // Keep the ref updated with latest function
  useEffect(() => {
    requestSuggestionsRef.current = requestSuggestions
  }, [requestSuggestions])

  const debounceEventHandler = useCallback(
    (searchString: string) => {
      // Clear any existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }

      // If we have a non-empty search string, immediately set loading state
      // This provides immediate feedback that a search is starting
      if (searchString && searchString.trim() !== '') {
        setIsLoading(true)
        // Hide suggestions from previous search to prevent showing stale results
        setShowSuggestions(false)
      }

      // Set new timeout using ref to avoid recreation
      debounceTimeoutRef.current = setTimeout(() => {
        requestSuggestionsRef.current(searchString)
      }, debounceTimeMs)
    },
    [debounceTimeMs] // Only depend on debounceTimeMs, not the full requestSuggestions
  )

  const updateRecentSearches = (suggestion: CatalogDtoSuggestionDto) => {
    const storedSearchHistory = localStorage.getItem('recentSearches')
    const searchHistory = storedSearchHistory
      ? JSON.parse(storedSearchHistory)
      : []

    // Check for duplicates by catalogDocumentId or programId
    const isDuplicate = searchHistory.some((item: CatalogDtoSuggestionDto) => {
      if (
        suggestion.document.catalogDocumentId &&
        item.document?.catalogDocumentId
      ) {
        return (
          suggestion.document.catalogDocumentId ===
          item.document.catalogDocumentId
        )
      }
      if (suggestion.document.programId && item.document?.programId) {
        return suggestion.document.programId === item.document.programId
      }
      return false
    })

    if (!isDuplicate) {
      searchHistory.unshift(suggestion)
      const updatedSearchHistory = searchHistory.slice(0, 5) // Keep max 5 items
      localStorage.setItem(
        'recentSearches',
        JSON.stringify(updatedSearchHistory)
      )
      // Dispatch custom event to notify other components about recent searches update
      window.dispatchEvent(new CustomEvent('recentSearchesUpdated'))
    }
  }

  // Helper function to render no results state
  const renderNoResults = (): ReactNode => {
    // AC8: Enhanced no results messaging based on search type
    let noResultsText: string

    if (hasAppliedDosageFilter && dosageFormFilter) {
      // Specific dosage form search
      noResultsText = `No results found for "${dosageFormFilter}". Try searching without the dosage form.`
    } else {
      // Check if this is a dosage form only search
      const queryWords = query.trim().toLowerCase().split(/\s+/)
      const isDosageFormOnlySearch =
        queryWords.length === 1 &&
        comprehensiveDosageForms.some(
          (form) =>
            form.toLowerCase() === queryWords[0] ||
            form.toLowerCase().includes(queryWords[0])
        )

      if (isDosageFormOnlySearch) {
        noResultsText = `No products found with "${query}" dosage form. Try searching for a specific product name.`
      } else {
        noResultsText = t('global_search:no_suggestions')
      }
    }

    return (
      <>
        <StyledAutoSuggestResultItem
          tabIndex={0}
          key={'no-results-title'}
          style={{
            color: ClinTheme.colors.black,
            fontSize: ClinTheme.fontSizes[2],
            backgroundColor: ClinTheme.colors.white
          }}
        >
          {noResultsText}
        </StyledAutoSuggestResultItem>
        <StyledAutoSuggestResultItem
          tabIndex={0}
          key={'no-results'}
          suggestionTextPadding={suggestionTextPadding}
          borderRadiusLastLiElement={borderRadiusLastLiElement}
          selected={highlightedIndex === suggestions.length}
          style={{ backgroundColor: 'rgb(247, 244, 241)' }}
        >
          {renderNoResultsLink()}
        </StyledAutoSuggestResultItem>
      </>
    )
  }

  const renderNoResultsHaveAlternatives = (): ReactNode => {
    // AC8: Helper text display logic
    // Include helper text for "generic + dosage form" searches
    // Remove helper text for "dosage form only" searches
    const queryWords = query.trim().toLowerCase().split(/\s+/)
    const isDosageFormOnlySearch =
      queryWords.length === 1 &&
      comprehensiveDosageForms.some(
        (form) =>
          form.toLowerCase() === queryWords[0] ||
          form.toLowerCase().includes(queryWords[0])
      )

    const shouldShowHelperText = isAliasesDisplayed && !isDosageFormOnlySearch

    return (
      <>
        {shouldShowHelperText && (
          <StyledAlternativesText>
            {showIcons && (
              <ClinIcon
                viewBox="0 0 24 24"
                iconSize={24}
                fillRule="evenodd"
                clipRule="nonzero"
                style={{ marginRight: ClinTheme.space[2] }}
                iconFill={ClinTheme.colors.primaryLight}
                iconName={ClinIconPathName.InfoOutline}
              />
            )}
            <ClinText fontSize={ClinTheme.fontSizes[2]}>
              <Trans
                i18nKey="product_catalogue:alternatives"
                values={{ query }}
                components={[<strong key="query-highlight" />]}
              />
            </ClinText>
          </StyledAlternativesText>
        )}
      </>
    )
  }

  // Helper function to render the no results link or text
  const renderNoResultsLink = (): ReactNode => {
    const text = isMaUser ? maUserNoResultsText : noResultsText
    const path = isMaUser
      ? `${maUserPath}?q=*`
      : noResultsPath || suggestionPath

    if (noResultsPath) {
      return (
        <Link
          to={path}
          style={{
            fontWeight: ClinTheme.fontWeights.medium,
            color: suggestionTextColor ?? ClinTheme.colors.black,
            fontSize: ClinTheme.fontSizes[2]
          }}
        >
          {text}
        </Link>
      )
    }

    return (
      <StyledNoResults
        onClick={() => {
          resetAll()
          handleOnSearch && handleOnSearch('*')
        }}
      >
        <ClinText
          fontWeight={ClinTheme.fontWeights.medium}
          fontSize={ClinTheme.fontSizes[2]}
        >
          {text}
        </ClinText>
      </StyledNoResults>
    )
  }

  // Helper function to render a single suggestion item
  const renderSuggestionItem = (
    suggestion: CatalogDtoSuggestionDto,
    index: number
  ): ReactNode => {
    // Separate out bolded text
    const matchingText = suggestion.text
    const genericName = suggestion.document.catalogItemName
    const brands = suggestion.document.brands

    // Strip tags to get generic or brand name
    const genericOrBrand = matchingText.replace(/<(\/)?(b)[^>]*>/g, '')

    // Determine which is the match
    const isGeneric = genericName === genericOrBrand

    // If its a brand show that brand as markup
    const programOrProduct =
      suggestion.document.derivedFrom === 'ManagedAccessProgram'
        ? `/programs/access-programs/${suggestion.document.programId}`
        : `/product/${suggestion.document.catalogDocumentId}`

    return (
      <StyledAutoSuggestResultItem
        key={`${suggestion.document.catalogDocumentId}-${index}`}
        tabIndex={0}
        selected={highlightedIndex === index}
        suggestionTextPadding={'0'}
        isHeader={isHeader}
        style={{ backgroundColor: ClinTheme.colors.white }}
      >
        <Link
          to={programOrProduct}
          tabIndex={-1}
          onClick={() => {
            featureExists && handleClick()
            updateRecentSearches(suggestion)
            analyticsServiceSingleton.trackEvent(
              AnalyticsEvent.SelectedProductFromGlobalSearch,
              {
                catalogDocumentId: suggestion.document.catalogDocumentId,
                catalogItemName: suggestion.document.catalogItemName,
                programId: suggestion.document.programId
              }
            )
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            padding: `${ClinTheme.space[2]}px ${ClinTheme.space[3]}px`
          }}
        >
          <div>
            <div
              tabIndex={-1}
              style={{
                color: ClinTheme.colors.black,
                fontSize: ClinTheme.fontSizes[1],
                lineHeight: '16px',
                fontWeight: ClinTheme.fontWeights.medium,
                marginBottom: ClinTheme.space[1]
              }}
              dangerouslySetInnerHTML={{
                // If its a generic display matching text in generic
                __html: isGeneric ? matchingText : genericName
              }}
            />
            <ClinText
              as="div"
              lineHeight="24px"
              color={ClinTheme.colors.darkGrey}
            >
              {renderBrands(brands, isGeneric, genericOrBrand, matchingText)}
            </ClinText>
          </div>
          {showIcons && (
            <ClinIcon
              iconSize={16}
              iconName={ClinIconPathName.ChevronRight}
              iconFill={ClinTheme.colors.tealishBlue}
            />
          )}
        </Link>
      </StyledAutoSuggestResultItem>
    )
  }

  const renderSuggestionCatalogItem = (
    catalogItem: ICatalogItemDto,
    index: number
  ): ReactNode => {
    return (
      <Fragment
        key={`desktop-variant-row-${catalogItem.itemId}-${catalogItem.item.sku}-${index}`}
      >
        {/* Main Row */}
        <StyledClinTableRow
          background={ClinTheme.colors.white}
          className={`main-row ${
            hoveredRow === `${catalogItem.itemId}-${catalogItem.item.sku}`
              ? 'highlighted'
              : ''
          }`}
          onMouseEnter={() =>
            handleMouseEnter(`${catalogItem.itemId}-${catalogItem.item.sku}`)
          }
          onMouseLeave={handleMouseLeave}
        >
          {columns.map((column) => (
            <StyledClinTableBodyCell
              key={`${column.name}-${catalogItem.itemId}-${catalogItem.item.sku}`}
              onClick={() =>
                handleSelectVariant && handleSelectVariant(catalogItem.item.sku)
              }
            >
              <FixedWidthCell
                width={column.width}
                minWidth={column.minWidth}
                maxWidth={column.maxWidth}
              >
                {column.renderValue ? column.renderValue(catalogItem) : null}
              </FixedWidthCell>
            </StyledClinTableBodyCell>
          ))}
        </StyledClinTableRow>

        {/* Empty Row */}
        <StyledClinTableRow
          key={`desktop-empty-row-${catalogItem.itemId}-${catalogItem.item.sku}-${index}`}
        >
          <ClinTableBodyCell
            colSpan={10}
            style={{ padding: '4px' }}
          ></ClinTableBodyCell>
        </StyledClinTableRow>
      </Fragment>
    )
  }

  // Helper function to render brand names
  const renderBrands = (
    brands: string[],
    isGeneric: boolean,
    genericOrBrand: string,
    matchingText: string
  ): ReactNode => {
    return brands.map((brand, idx) => {
      if (!isGeneric && brand === genericOrBrand) {
        return (
          <span
            key={`${brand}-${idx}`}
            dangerouslySetInnerHTML={{ __html: matchingText }}
          />
        )
      }

      return idx !== brands.length - 1 ? (
        <span key={`${brand}-${idx}`}>{brand}, </span>
      ) : (
        <span key={`${brand}-${idx}`}>{brand}</span>
      )
    })
  }

  // Helper function to render "View the Product Catalogue" link
  const renderViewCatalogueLink = (): ReactNode => {
    const path = isMaUser ? maUserPath : suggestionPath
    return (
      <StyledAutoSuggestResultItem
        suggestionTextPadding={suggestionTextPadding}
        borderRadiusLastLiElement={borderRadiusLastLiElement}
        tabIndex={0}
        key={'view-catalogue'}
        selected={highlightedIndex === (recentSearches?.length || 0)}
        style={{ backgroundColor: ClinTheme.colors.lightGrey }}
      >
        <Link
          to={path}
          onClick={() => {
            resetAll()
          }}
          style={{
            fontWeight: ClinTheme.fontWeights.medium,
            color: suggestionTextColor ?? ClinTheme.colors.black,
            fontSize: ClinTheme.fontSizes[2]
          }}
        >
          <StyledLinkWithIcon>
            <ClinText
              fontSize={ClinTheme.fontSizes[2]}
              fontWeight={ClinTheme.fontWeights.medium}
              as="div"
              color={ClinTheme.colors.tealishBlue}
            >
              View the Product Catalogue
            </ClinText>
            {showIcons && (
              <ClinIcon
                style={{ marginLeft: '8px' }}
                iconSize={20}
                iconName={ClinIconPathName.ArrowRight}
                iconFill={ClinTheme.colors.tealishBlue}
              />
            )}
          </StyledLinkWithIcon>
        </Link>
      </StyledAutoSuggestResultItem>
    )
  }

  // Helper function to render the "Looking for something else?" link
  const renderSuggestionLink = (): ReactNode => {
    const path = isMaUser ? maUserPath : suggestionPath
    return (
      <StyledAutoSuggestResultItem
        suggestionTextPadding={suggestionTextPadding}
        borderRadiusLastLiElement={borderRadiusLastLiElement}
        tabIndex={0}
        key={'suggestion-link'}
        selected={highlightedIndex === suggestions.length}
        style={{ backgroundColor: '#F7F4F1' }}
      >
        <Link
          to={path}
          onClick={() => {
            resetAll()
          }}
          style={{
            fontWeight: ClinTheme.fontWeights.medium,
            color: suggestionTextColor ?? ClinTheme.colors.black,
            fontSize: ClinTheme.fontSizes[2]
          }}
        >
          <StyledLinkWithIcon>
            <ClinText
              fontSize={ClinTheme.fontSizes[2]}
              fontWeight={ClinTheme.fontWeights.medium}
              as="div"
              color={ClinTheme.colors.tealishBlue}
            >
              {suggestionText}
            </ClinText>
            {showIcons && (
              <ClinIcon
                style={{ marginLeft: '8px' }}
                iconSize={20}
                iconName={ClinIconPathName.ArrowRight}
                iconFill={ClinTheme.colors.tealishBlue}
              />
            )}
          </StyledLinkWithIcon>
        </Link>
      </StyledAutoSuggestResultItem>
    )
  }

  // Helper function to determine if programs should be prioritized based on match quality
  const shouldPrioritizePrograms = (
    query: string,
    grouped: GroupedSuggestions
  ): boolean => {
    // Only apply this logic if query is at least 3 characters
    if (!query || query.trim().length < 3) {
      return false
    }

    const queryStart = query.trim().toLowerCase().substring(0, 3)

    // Check if any programs have names starting with the same 3 characters
    const programMatches = grouped.programs.some((program) =>
      program.document.catalogItemName?.toLowerCase().startsWith(queryStart)
    )

    // Check if any products have names starting with the same 3 characters
    const productMatches = grouped.products.some((product) =>
      product.document.catalogItemName?.toLowerCase().startsWith(queryStart)
    )

    // According to AC: If programs have a closer match (first 3 chars), show programs first
    // If products have a closer match, show products first
    // If both or neither have matches, default to products first
    return programMatches && !productMatches
  }

  // Helper function to render section header with count
  const renderSectionHeader = (
    title: string,
    count: number,
    isFirstSection: boolean = false
  ): ReactNode => {
    return (
      <StyledSectionHeader
        key={`section-header-${title}`}
        isFirstSection={isFirstSection}
        style={{ backgroundColor: ClinTheme.colors.white }}
      >
        <StyledSectionTitle>{title}</StyledSectionTitle>
        {!isAliasesDisplayed && (
          <StyledCounterContainer>
            <StyledCounterNumber>{count}</StyledCounterNumber>
          </StyledCounterContainer>
        )}
      </StyledSectionHeader>
    )
  }

  // Function to render recent searches grouped by type
  const renderRecentSearches = (): ReactNode[] => {
    if (!recentSearches || recentSearches.length === 0) {
      return []
    }

    const elements = []

    // Add header for recent searches with info icon - left aligned
    elements.push(
      <StyledRecentSearchHeader key="recent-searches-header" tabIndex={0}>
        {showIcons && (
          <ClinIcon
            viewBox="0 0 24 24"
            iconSize={24}
            fillRule="evenodd"
            clipRule="nonzero"
            iconFill="#7239FF"
            iconName={ClinIconPathName.InfoOutline}
          />
        )}
        Recently searched products
      </StyledRecentSearchHeader>
    )

    // Group recent searches by Products and Programs
    const products = recentSearches.filter(
      (item) => item.document.derivedFrom !== 'ManagedAccessProgram'
    )
    const programs = recentSearches.filter(
      (item) => item.document.derivedFrom === 'ManagedAccessProgram'
    )

    let itemIndex = 0

    // Render Products section (without section header for recent searches)
    if (products.length > 0) {
      products.forEach((product) => {
        elements.push(
          <React.Fragment
            key={`recent-product-${
              product.document.catalogDocumentId || product.document.programId
            }-${itemIndex}`}
          >
            {renderSuggestionItem(product, itemIndex)}
          </React.Fragment>
        )
        itemIndex++
      })
    }

    // Render Programs section (without section header for recent searches)
    if (programs.length > 0) {
      programs.forEach((program) => {
        elements.push(
          <React.Fragment
            key={`recent-program-${
              program.document.catalogDocumentId || program.document.programId
            }-${itemIndex}`}
          >
            {renderSuggestionItem(program, itemIndex)}
          </React.Fragment>
        )
        itemIndex++
      })
    }

    // Add the "View the Product Catalogue" link at the end
    elements.push(
      <React.Fragment key="view-catalogue-link">
        {renderViewCatalogueLink()}
      </React.Fragment>
    )

    return elements
  }

  // Main rendering function with grouped suggestions by type
  const renderSuggestions = (
    items: CatalogDtoSuggestionDto[]
  ): ReactNode | ReactNode[] => {
    // If input is focused but query is empty, show recent searches only if enabled
    if (isFocused && (!query || query.trim() === '') && showRecentSearches) {
      return renderRecentSearches()
    }

    const elements = []

    // CLOS-13182: Show "No results matching..." message when primary search failed
    // but alias search found alternatives
    if (
      primarySearchReturnedNoResults &&
      isAliasesDisplayed &&
      items &&
      items.length > 0 &&
      !isLoading &&
      hasSearched &&
      query &&
      query.trim() !== '' &&
      showNoResults
    ) {
      // Show the "No results matching..." message first
      elements.push(
        <StyledAutoSuggestResultItem
          tabIndex={0}
          key={'no-results-title-with-alternatives'}
          style={{
            color: ClinTheme.colors.black,
            fontSize: ClinTheme.fontSizes[2],
            backgroundColor: ClinTheme.colors.white
          }}
        >
          <Trans
            i18nKey="product_catalogue:alternatives"
            values={{ query }}
            components={[<strong key="query-highlight" />]}
          />
        </StyledAutoSuggestResultItem>
      )
    }

    // Handle empty results - only show "no results" if we have a query and we're not loading
    // and we've actually completed a search attempt and showNoResults is true
    // Don't show if we're showing alternatives (primary search failed but alias search succeeded)
    if (
      items &&
      items.length === 0 &&
      !isLoading &&
      hasSearched &&
      query &&
      query.trim() !== '' &&
      showNoResults &&
      !(primarySearchReturnedNoResults && isAliasesDisplayed)
    ) {
      return renderNoResults()
    }

    // Handle items with suggestions
    if (!items || items.length === 0) {
      return elements.length > 0 ? elements : null
    }

    // Add alternatives text when alias search found results (includeAliases: true was used)
    // This shows when alias search actually found results, similar to ProductSearch behavior
    // Note: We don't show this if we already showed the "No results matching..." message above
    // For homepage (showNoResults=false), always show alternatives text when alias search found results
    // The "No results matching..." message only shows when showNoResults=true, so we check that
    const shouldShowAlternatives =
      isAliasesDisplayed &&
      items.length > 0 &&
      !(primarySearchReturnedNoResults && showNoResults)
    if (shouldShowAlternatives) {
      const alternativesElement = renderNoResultsHaveAlternatives()
      if (alternativesElement) {
        elements.push(
          <React.Fragment key="alternatives">
            {alternativesElement}
          </React.Fragment>
        )
      }
    }

    // Group items by type and calculate counts
    // Business Logic:
    // - Single word searches (e.g., "solution"): Show API counts (unfiltered total)
    // - Multi-word searches with dosage forms (e.g., "progesterone pessary"): Show filtered counts (displayed items)
    // - Header context: Always use API counts for consistency
    const queryWords = query.trim().split(/\s+/)
    const isSingleWordSearch = queryWords.length === 1

    // Use API counts when:
    // 1. Header context (always use API counts)
    // 2. Single word searches (e.g., "solution" - user wants all solutions, not filtered)
    // 3. No dosage filtering applied (regular product searches)
    // Use filtered counts only for multi-word searches with active dosage filtering
    const shouldUseApiCounts =
      isHeader || isSingleWordSearch || !hasAppliedDosageFilter

    const currentProductsCount = shouldUseApiCounts
      ? apiProductsCount
      : items.filter(
          (item: CatalogDtoSuggestionDto) =>
            item.document.derivedFrom !== 'ManagedAccessProgram'
        ).length
    const currentProgramsCount = shouldUseApiCounts
      ? apiProgramsCount
      : items.filter(
          (item: CatalogDtoSuggestionDto) =>
            item.document.derivedFrom === 'ManagedAccessProgram'
        ).length

    const grouped = groupSuggestionsByType(
      items,
      currentProductsCount,
      currentProgramsCount
    )

    let itemIndex = 0

    // Determine which section is first
    const hasProducts = grouped.products.length > 0
    const hasPrograms = grouped.programs.length > 0
    if (isExactMatch) {
      const itemName = items[0].document.catalogItemName
      const title = itemName.charAt(0).toUpperCase() + itemName.slice(1)

      // Don't show generic title for alternative search results
      if (!isAliasesDisplayed) {
        // Use filteredCatalogItems.length if available, otherwise use 1 (since we know there's 1 product)
        const count =
          filteredCatalogItems.length > 0 ? filteredCatalogItems.length : 1
        elements.push(
          <React.Fragment key="exact-match-header">
            {renderSectionHeader(title, count, true)}
          </React.Fragment>
        )

        // Add dosage form helper text
        const renderDosageFormHelperText = () => {
          const { productName, dosageForm } = parseSearchQueryLocal(query, true)

          const queryWords = query.trim().toLowerCase().split(/\s+/)
          const isDosageFormOnlySearch =
            queryWords.length === 1 &&
            comprehensiveDosageForms.some(
              (form) =>
                form.toLowerCase() === queryWords[0] ||
                form.toLowerCase().includes(queryWords[0])
            )

          if (productName && dosageForm && !isDosageFormOnlySearch) {
            const originalItemsCount = catalogItemsWithPrice.filter(
              (item) =>
                item.availableStatus !== ProductItemAvailabilityStatus.HIDDEN
            ).length

            // Check if we actually have filtered results by testing the dosage form filter
            const filterTerm = dosageForm.toLowerCase().trim()
            const actuallyFilteredItems = catalogItemsWithPrice
              .filter(
                (item) =>
                  item.availableStatus !== ProductItemAvailabilityStatus.HIDDEN
              )
              .filter((item) => {
                const formulation = item.item?.formulation?.toLowerCase() || ''

                // Direct inclusion matching (exact or substring)
                if (formulation.includes(filterTerm)) {
                  return true
                }

                // Handle hyphenated variations (e.g., "prolonged-release tablet")
                const normalizedFormulation = formulation.replace(/-/g, ' ')
                const normalizedFilter = filterTerm.replace(/-/g, ' ')
                if (normalizedFormulation.includes(normalizedFilter)) {
                  return true
                }

                // Enhanced partial matching - check if filter term partially matches any dosage form in formulation
                const formulationWords = formulation.split(/\s+/)

                for (const word of formulationWords) {
                  // Try to find if this word partially matches our filter term
                  const partialMatch =
                    findPartialDosageFormMatchLocal(filterTerm)
                  if (
                    partialMatch &&
                    word.includes(partialMatch.toLowerCase())
                  ) {
                    return true
                  }

                  // Also check if the word in formulation partially matches the filter
                  const wordMatch = findPartialDosageFormMatchLocal(word)
                  if (
                    wordMatch &&
                    filterTerm.includes(wordMatch.toLowerCase())
                  ) {
                    return true
                  }
                }

                return false
              })

            const hasActualFilteredResults = actuallyFilteredItems.length > 0
            const isShowingFallbackResults =
              hasAppliedDosageFilter &&
              filteredCatalogItems.length === originalItemsCount &&
              originalItemsCount > 0 &&
              !hasActualFilteredResults

            const helperText =
              hasActualFilteredResults && !isShowingFallbackResults
                ? `Results when filtered by "${dosageForm}" below`
                : `No results when filtered by "${dosageForm}", please see available results below`

            return (
              <ClinText
                as="div"
                fontSize={ClinTheme.fontSizes[1]}
                color={ClinTheme.colors.darkGrey}
                marginTop={ClinTheme.space[3]}
                marginBottom={ClinTheme.space[2]}
                marginLeft={ClinTheme.space[2]}
                marginRight={ClinTheme.space[3]}
              >
                {helperText}
              </ClinText>
            )
          }
          return null
        }

        elements.push(
          <React.Fragment key="dosage-form-helper-text">
            {renderDosageFormHelperText()}
          </React.Fragment>
        )
      }
      if (isExactMatchLoading && filteredCatalogItems.length === 0) {
        elements.push(
          <StyledLoadingSpinnerWrapper key="exact-match-loading">
            <ClinSpinner size={ClinTheme.space[4]} />
          </StyledLoadingSpinnerWrapper>
        )
      }
      // Only render variants table/container if the exact match is a product
      if (product?.derivedFrom !== 'ManagedAccessProgram') {
        elements.push(
          <StyledDesktopWrapper key="exact-match-desktop">
            <StyledClinTable
              borderColapseSeparate={true}
              className="TableOverflowVisible"
            >
              {filteredCatalogItems.map((item, idx) =>
                renderSuggestionCatalogItem(item, idx)
              )}
            </StyledClinTable>
          </StyledDesktopWrapper>
        )
        elements.push(
          <StyledMobileWrapper key="exact-match-mobile">
            {filteredCatalogItems.map((catalogItem, index) => (
              <MobileItem
                key={`mobile-variant-${catalogItem.itemId}-${catalogItem.item.sku}-${index}`}
                catalogItem={catalogItem}
                userCountry={portfolioCountryCode}
                userRole={userRole}
                product={product!}
                catalogueItemStatus={getCatalogueItemStatus(
                  catalogItem,
                  portfolioCountryCode,
                  catalogItem.priceAvailability
                )}
                catalogueItemDeliveryDate={getCatalogueItemDeliveryDate(
                  catalogItem,
                  portfolioCountryCode
                )}
                setIsDrawerOpen={setIsDrawerOpen}
                handleGoToBasket={handleGoToBasket}
                handleProceedToCheckout={
                  isNewCheckoutPage
                    ? handleProceedToCheckout
                    : handleProceedToCheckoutOld
                }
                handleSelectVariant={handleSelectVariant}
                viewButtonText={t('common:buttons.view')}
              />
            ))}
          </StyledMobileWrapper>
        )
      }
    } else {
      // Determine the display order based on match quality
      const prioritizePrograms = shouldPrioritizePrograms(query, grouped)

      if (prioritizePrograms) {
        // Render Access Programs section first
        if (hasPrograms) {
          if (!isAliasesDisplayed) {
            elements.push(
              <React.Fragment key="programs-header">
                {renderSectionHeader(
                  'Access programs',
                  grouped.programsCount || grouped.programs.length,
                  true
                )}
              </React.Fragment>
            )
          }
          grouped.programs.forEach((program) => {
            elements.push(
              <React.Fragment
                key={`program-${
                  program.document.catalogDocumentId ||
                  program.document.programId
                }-${itemIndex}`}
              >
                {renderSuggestionItem(program, itemIndex)}
              </React.Fragment>
            )
            itemIndex++
          })
        }

        // Render Products section second
        if (hasProducts) {
          if (!isAliasesDisplayed) {
            elements.push(
              <React.Fragment key="products-header">
                {renderSectionHeader(
                  'Products',
                  grouped.productsCount || grouped.products.length,
                  !hasPrograms
                )}
              </React.Fragment>
            )
          }
          grouped.products.forEach((product) => {
            elements.push(
              <React.Fragment
                key={`product-${
                  product.document.catalogDocumentId ||
                  product.document.programId
                }-${itemIndex}`}
              >
                {renderSuggestionItem(product, itemIndex)}
              </React.Fragment>
            )
            itemIndex++
          })
        }
      } else {
        // Render Products section first (default behavior)
        if (hasProducts) {
          if (!isAliasesDisplayed) {
            elements.push(
              <React.Fragment key="products-header">
                {renderSectionHeader(
                  'Products',
                  grouped.productsCount || grouped.products.length,
                  true
                )}
              </React.Fragment>
            )
          }
          grouped.products.forEach((product) => {
            elements.push(
              <React.Fragment
                key={`product-${
                  product.document.catalogDocumentId ||
                  product.document.programId
                }-${itemIndex}`}
              >
                {renderSuggestionItem(product, itemIndex)}
              </React.Fragment>
            )
            itemIndex++
          })
        }

        // Render Access Programs section second
        if (hasPrograms) {
          if (!isAliasesDisplayed) {
            elements.push(
              <React.Fragment key="programs-header">
                {renderSectionHeader(
                  'Access programs',
                  grouped.programsCount || grouped.programs.length,
                  !hasProducts
                )}
              </React.Fragment>
            )
          }
          grouped.programs.forEach((program) => {
            elements.push(
              <React.Fragment
                key={`program-${
                  program.document.catalogDocumentId ||
                  program.document.programId
                }-${itemIndex}`}
              >
                {renderSuggestionItem(program, itemIndex)}
              </React.Fragment>
            )
            itemIndex++
          })
        }
      }
    }

    // Add the "Looking for something else?" link at the end for regular suggestions
    elements.push(
      <React.Fragment key="suggestion-link">
        {renderSuggestionLink()}
      </React.Fragment>
    )

    return elements
  }

  const handleEscapeKey = () => {
    resetAll()
  }

  const handleDownOrTabKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    e.preventDefault()
    const INITIAL_IDX = 0
    const idx = highlightedIndex
    const nextIdx = idx !== undefined ? idx + 1 : INITIAL_IDX

    if (nextIdx < suggestions.length) {
      setHighlightedIndex(nextIdx)
    } else {
      setHighlightedIndex(INITIAL_IDX)
    }
  }

  const handleUpOrShiftTabKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    e.preventDefault()
    const lastIdx = suggestions.length - 1
    const idx = highlightedIndex
    const prevIdx = idx !== undefined ? idx - 1 : lastIdx

    if (prevIdx >= 0) {
      setHighlightedIndex(prevIdx)
    } else {
      setHighlightedIndex(lastIdx)
    }
  }

  const handleEnterKey = () => {
    if (highlightedIndex >= 0) {
      handleSelect && handleSelect(highlightedIndex)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const UP = 'ArrowUp'
    const DOWN = 'ArrowDown'
    const ENTER = 'Enter'
    const ESCAPE = 'Escape'
    const TAB = 'Tab'

    switch (e.key) {
      case ESCAPE:
        handleEscapeKey()
        break
      case DOWN:
      case TAB:
        handleDownOrTabKey(e)
        break
      case UP:
        handleUpOrShiftTabKey(e)
        break
      case ENTER:
        handleEnterKey()
        break
      default:
        // Handle shift+tab as a special case
        if (e.shiftKey && e.key === TAB) {
          handleUpOrShiftTabKey(e)
        }
        break
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const queryStr = e.target.value
    setQuery(queryStr)

    // Reset hasSearched when user changes query to prevent showing stale "no results"
    // This should happen for any change, not just when query becomes empty
    if (queryStr.trim() === '') {
      setHasSearched(false)
      // Reset enhanced search states when query is cleared
      setDosageFormFilter('')
      setHasAppliedDosageFilter(false)
      // Reset primary search failure state
      setPrimarySearchReturnedNoResults(false)
      // Immediately hide suggestions when query becomes empty to prevent empty dropdown
      setShowSuggestions(
        isFocused &&
          showRecentSearches &&
          !!(recentSearches && recentSearches.length > 0)
      )
    } else {
      // Reset hasSearched to false when user starts typing a new query
      // This prevents showing "No results" from previous search while new search is loading
      setHasSearched(false)
      // Also clear any previous "not found" state immediately
      setIsNotFound && setIsNotFound(false)
      setIsAliasesDisplayed && setIsAliasesDisplayed(false)
      // Reset primary search failure state when user starts typing new query
      setPrimarySearchReturnedNoResults(false)
    }

    debounceEventHandler(queryStr)
    handleOnChange && handleOnChange(queryStr)
    setCurrentQuery && setCurrentQuery(queryStr)
  }

  const handleFocus = () => {
    setIsFocused(true)
    // Show recent searches if query is empty and we have recent searches and showRecentSearches is enabled
    if (
      showRecentSearches &&
      (!query || query.trim() === '') &&
      recentSearches &&
      recentSearches.length > 0
    ) {
      setShowSuggestions(true)
    }
  }

  const handleBlur = () => {
    // Delay hiding to allow clicks on suggestions
    blurTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setIsFocused(false)
        if (!query || query.trim() === '') {
          setShowSuggestions(false)
        }
      }
    }, 300) // Increased delay to 300ms
  }

  //clos new feature tooltip if user click on link of suggested products
  const handleClick = () => {
    const element = document.getElementById('global-search-new-user')
    if (element) {
      element.click()
    }
  }

  const handleEnter = () => {
    featureExists && handleClick()
    const userRolePath = isMaUser ? `/results` : `/products/catalogue`
    handleOnSearch
      ? handleOnSearch(query)
      : history.push(`${userRolePath}?q=${encodeURIComponent(query)}`)
  }

  const handleSelect = (selectedIndex: number) => {
    const userRolePath = isMaUser ? `/programs/access-programs/` : `/product/`
    const itemId = isMaUser ? `programId` : `catalogDocumentId`
    const currentSuggestions =
      isFocused && (!query || query.trim() === '')
        ? recentSearches || []
        : suggestions
    if (currentSuggestions[selectedIndex]) {
      updateRecentSearches(currentSuggestions[selectedIndex])
      history.push(
        `${userRolePath}${currentSuggestions[selectedIndex].document[itemId]}`
      )
    }
  }

  // Detect outside click
  const parentNode = useRef<HTMLDivElement>(null)

  const handleClickedOutside = (event: MouseEvent) => {
    if (parentNode?.current?.contains(event.target as Node)) {
      // inside click
      return
    }
    // outside click - clear results
    resetAll()
  }

  const filterHiddenVariants = useCallback(() => {
    // Filter out hidden items first
    let filtered = catalogItemsWithPrice.filter(
      (item) => item.availableStatus !== ProductItemAvailabilityStatus.HIDDEN
    )

    // Apply dosage form filtering for exact matches when a dosage form is detected
    if (isExactMatch && dosageFormFilter && hasAppliedDosageFilter) {
      const filterTerm = dosageFormFilter.toLowerCase().trim()

      const filteredByDosageForm = filtered.filter((item) => {
        const formulation = item.item?.formulation?.toLowerCase() || ''

        // Direct inclusion matching (exact or substring)
        if (formulation.includes(filterTerm)) {
          return true
        }

        // Handle hyphenated variations (e.g., "prolonged-release tablet")
        const normalizedFormulation = formulation.replace(/-/g, ' ')
        const normalizedFilter = filterTerm.replace(/-/g, ' ')
        if (normalizedFormulation.includes(normalizedFilter)) {
          return true
        }

        // Enhanced partial matching - check if filter term partially matches any dosage form in formulation
        const formulationWords = formulation.split(/\s+/)

        for (const word of formulationWords) {
          // Try to find if this word partially matches our filter term
          const partialMatch = findPartialDosageFormMatchLocal(filterTerm)
          if (partialMatch && word.includes(partialMatch.toLowerCase())) {
            return true
          }

          // Also check if the word in formulation partially matches the filter
          const wordMatch = findPartialDosageFormMatchLocal(word)
          if (wordMatch && filterTerm.includes(wordMatch.toLowerCase())) {
            return true
          }
        }

        return false
      })

      // If no items match the dosage form filter, show all variants instead of empty list
      if (filteredByDosageForm.length === 0) {
        // Keep the original filtered list (just hidden items removed)
      } else {
        // Use the dosage form filtered results
        filtered = filteredByDosageForm
      }
    }

    return filtered
  }, [
    catalogItemsWithPrice,
    isExactMatch,
    dosageFormFilter,
    hasAppliedDosageFilter,
    findPartialDosageFormMatchLocal
  ])

  const handleProductError = useErrorMessage(
    'There was an error fetching the product.'
  )

  const handleWarehouseError = useErrorMessage(
    'There was an error fetching warehouse details.'
  )

  const handleMouseEnter = (id: string) => {
    setHoveredRow(id)
  }

  const handleMouseLeave = () => {
    setHoveredRow(null)
  }

  const handleSelectVariant = (sku: string) => {
    history.push(`/product/${product?.catalogDocumentId}/variant/${sku}`)
  }

  const handleGoToBasket = () => {
    history.push('/basket')
  }

  const handleProceedToCheckout = () => {
    if (isAusUser(portfolioCountryCode)) {
      history.push('/one-step-checkout')
    } else if (enableNewCheckoutDrawer) {
      setIsCheckoutDrawerOpen && setIsCheckoutDrawerOpen(true)
    } else {
      history.push('/checkout')
    }
  }

  const handleProceedToCheckoutOld = () => {
    history.push('/basket')
  }

  const handleGoToProductDetails = () => {
    history.push('/basket')
  }

  useEffect(() => {
    document.addEventListener('click', handleClickedOutside)
    return () => document.removeEventListener('click', handleClickedOutside)
  })

  useEffect(() => {
    resetAll()
    // Clear any pending timeouts when location changes
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current)
      blurTimeoutRef.current = null
    }
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
      debounceTimeoutRef.current = null
    }
    return () => cancelGetProductsSuggestion()
  }, [location.pathname])

  useEffect(() => {
    setQuery(initialQuery)
    setCurrentQuery && setCurrentQuery(initialQuery)
  }, [initialQuery, setCurrentQuery])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current)
        blurTimeoutRef.current = null
      }
      // Clear debounce timeout on cleanup
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    setFilteredCatalogItems(filterHiddenVariants())
    setIsExactMatchLoading(false)
  }, [catalogItemsWithPrice, filterHiddenVariants])

  //Get price
  useEffect(() => {
    if (catalogItems.length) {
      const itemsWithPrice = catalogItems.map((item) => ({
        ...item,
        unitPrice: undefined,
        currencyCode: undefined
      }))

      setCatalogItemsWithPrice(itemsWithPrice)
      // Reset prices loaded flag when new catalogItems are set
      pricesLoadedRef.current = false
    }
  }, [catalogItems])

  useEffect(() => {
    let isCanceledRequest = false
    if (
      catalogItemsWithPrice.length &&
      defaultShippingAddress &&
      !pricesLoadedRef.current
    ) {
      const itemsToPrice = catalogItemsWithPrice.filter(
        (item) =>
          item.availableStatus === ProductItemAvailabilityStatus.AVAILABLE
      )
      if (itemsToPrice.length > 0) {
        void Promise.allSettled(
          itemsToPrice.map((item) =>
            getPriceAndAvailabilityForSku(
              item.sku,
              defaultShippingAddress.addressId
            )
          )
        ).then((results) => {
          if (isCanceledRequest) {
            dispatch(clearAnnounceEvent())

            return
          }

          const hasRejections = results.some(
            (result) => result.status === 'rejected'
          )
          const updatedItems = catalogItemsWithPrice.map((item) => {
            const index = itemsToPrice.findIndex((i) => i.sku === item.sku)
            if (index !== -1) {
              const result = results[index]
              if (result.status === 'fulfilled') {
                const priceData = result.value.data

                return {
                  ...item,
                  unitPrice: Number.parseFloat(priceData.listPrice),
                  currencyCode: priceData.currencyCode,
                  priceAvailability: priceData
                }
              } else {
                return {
                  ...item,
                  unitPrice: undefined,
                  currencyCode: undefined,
                  priceAvailability: undefined
                }
              }
            }
            return item
          })
          setCatalogItemsWithPrice(updatedItems)
          pricesLoadedRef.current = true
          if (hasRejections) {
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                `We could not retrieve the price or packs available for some items, please try and refresh your page`
              )
            )
          }
        })
      } else {
        pricesLoadedRef.current = true
      }
    }
    return () => {
      isCanceledRequest = true
      cancelGetPriceAndAvailabilityForSku()
    }
  }, [catalogItemsWithPrice, defaultShippingAddress, dispatch])

  if (pathsToHide && matchInArray(location.pathname, pathsToHide)) {
    return null
  }

  return (
    <>
      <StyledAutoSuggestWrapper ref={parentNode}>
        {searchComponent({
          query,
          isLoading,
          showSuggestions: showSuggestions,
          suggestions,
          renderSuggestions,
          renderNoResultsHaveAlternatives,
          placeholderText,
          isHeader,
          handleChange,
          handleKeyDown,
          handleEnter,
          handleFocus,
          handleBlur
        } as IAutoSuggestProps)}
      </StyledAutoSuggestWrapper>
      {enableNewBasketDrawer && (
        <ClinDrawer
          open={isDrawerOpen}
          width={512}
          onClose={() => setIsDrawerOpen(false)}
        >
          <BasketDrawer
            handleClose={() => setIsDrawerOpen(false)}
            handleCheckoutOpen={() => setIsCheckoutDrawerOpen(true)}
          />
        </ClinDrawer>
      )}
      {enableNewCheckoutDrawer && (
        <ClinDrawer
          open={isCheckoutDrawerOpen}
          width={512}
          onClose={() => setIsCheckoutDrawerOpen(false)}
        >
          <CheckoutDrawer handleClose={() => setIsCheckoutDrawerOpen(false)} />
        </ClinDrawer>
      )}
    </>
  )
}
